import { CloseCircleFilled, DeleteOutlined, PauseCircleFilled, PlayCircleFilled } from "@ant-design/icons";
import { DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY } from "@org-avp/avp-avengers-ui-shared-utils";
import { Button, Progress, ProgressProps, Tooltip } from "antd";
import { FC, MouseEventHandler, useCallback, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "./SingleTaskDisplay.module.less";
import { TaskStatus } from "./use-tasks-collector-helper";

export interface SingleTaskDisplayProps {
  task: TaskStatus;
  displayInTitleBar?: boolean;
  hideTask?: (task: TaskStatus) => void;
}

export const SingleTaskDisplay: FC<SingleTaskDisplayProps> = ({ task, displayInTitleBar, hideTask }) => {
  useTranslation();

  const { total, success, failed } = task;
  const pending = useMemo(() => task.total - task.success - task.failed, [task]);

  const progressPercent = useMemo(() => {
    return ((success + failed) / total) * 100;
  }, [failed, success, total]);

  const progressStatus = useMemo<NonNullable<ProgressProps["status"]>>(
    () => {
      if (success === total)
        return "success";
      else if (pending === 0)
        return "exception";
      else if (task.paused)
        return "normal";
      else
        return "active";
    },
    [pending, success, task.paused, total],
  );

  const onHideTask = useCallback<MouseEventHandler>(
    () => {
      if (hideTask) hideTask(task);
    },
    [hideTask, task],
  );

  return (
    <div className={displayInTitleBar ? styles.titleBarTaskDisplay : styles.popoverTaskDisplay}>
      <span><Trans ns="app" i18nKey={task.descriptorTKey}/></span>
      <div className={styles.progressBarLine}>
        <Progress
          size={displayInTitleBar ? "small" : "default"}
          className={styles.progressBar}
          percent={progressPercent}
          showInfo={false}
          status={progressStatus}
        />
        {!displayInTitleBar && (
          <div className={styles.actionButtons}>
            {progressStatus === "normal" && task.onResume && (
              <Tooltip title={<Trans ns="app" i18nKey="titleBar.taskDisplay.actions.resume"/>} mouseEnterDelay={DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY}>
                <Button type="text" size="small" shape="circle" icon={<PlayCircleFilled/>} onClick={task.onResume}/>
              </Tooltip>
            )}
            {progressStatus === "active" && task.onPause && (
              <Tooltip title={<Trans ns="app" i18nKey="titleBar.taskDisplay.actions.pause"/>} mouseEnterDelay={DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY}>
                <Button type="text" size="small" shape="circle" icon={<PauseCircleFilled/>} onClick={task.onPause}/>
              </Tooltip>
            )}
            {(progressStatus === "active" || progressStatus === "normal") && task.onCancel && (
              <Tooltip title={<Trans ns="app" i18nKey="titleBar.taskDisplay.actions.cancel"/>} mouseEnterDelay={DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY}>
                <Button type="text" size="small" shape="circle" icon={<CloseCircleFilled/>} onClick={task.onCancel}/>
              </Tooltip>
            )}
            {(progressStatus === "success" || progressStatus === "exception") && (
              <Tooltip title={<Trans ns="app" i18nKey="titleBar.taskDisplay.actions.hide"/>} mouseEnterDelay={DEFAULT_TOOLBAR_MOUSE_ENTER_DELAY}>
                <Button type="text" size="small" shape="circle" icon={<DeleteOutlined/>} onClick={onHideTask}/>
              </Tooltip>
            )}
          </div>
        )}
      </div>
      {!displayInTitleBar && (
        <div className={styles.progressInfo}>
          {progressStatus === "normal" && <Trans ns="app" i18nKey="titleBar.taskDisplay.taskInfo.paused"/>}
          {progressStatus !== "normal" && (
            <>
              <Trans ns="app" i18nKey="titleBar.taskDisplay.taskInfo.success" values={{ count: success }}/>
              {", "}
              <Trans ns="app" i18nKey="titleBar.taskDisplay.taskInfo.failed" values={{ count: failed }}/>
              {pending !== 0 && (
                <>
                  {", "}
                  <Trans ns="app" i18nKey="titleBar.taskDisplay.taskInfo.pending" values={{ count: pending }}/>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
